import type { Placement } from "@popperjs/core";
import { useEffect, useRef } from "react";
import styled, { css } from "styled-components";
import { Button } from "@fiberplane/ui";
import { AnimatePresence } from "framer-motion";

import { cancelEvent, noop } from "../../../utils";
import { SelectOption as Option } from "./SelectOption";
import { useOpen } from "../../../hooks";
import { AttachedPopup, AnimatedCaretIcon } from "..";

type Props = {
  autoFocus?: boolean;
  className?: string;

  /**
   * If `true`, selection is disabled.
   */
  disabled?: boolean;

  /**
   * Optional placement hint where to open the dropdown containing the options.
   */
  dropdownPlacement?: Placement;

  /**
   * HTML `name` used for grouping the options in the DOM.
   */
  name: string;

  onChange: (value: string) => void;

  /**
   * List of all options to select from.
   */
  options: Array<SelectOption>;

  /**
   * Optional placeholder to display if no option is selected.
   */
  placeholder?: string;

  leadingIcon?: React.ElementType<React.SVGProps<SVGSVGElement>>;
  trailingIcon?: React.ElementType<React.SVGProps<SVGSVGElement>>;

  /**
   * The value of the selected option, if any.
   */
  value?: string;
};

export type SelectOption = {
  children: React.ReactNode;
  value: string;
};

export function Select({
  autoFocus,
  className,
  disabled = false,
  dropdownPlacement,
  name,
  onChange,
  options,
  placeholder,
  leadingIcon: LeadingIcon,
  trailingIcon: TrailingIcon,
  value,
  ...attributes
}: Props): JSX.Element {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { opened, setOpened, modalRef } = useOpen(containerRef);

  useEffect(() => {
    if (autoFocus) {
      containerRef.current?.focus();
    }
  }, [autoFocus]);

  const selectedOption = value
    ? options.find((option) => option.value === value)
    : undefined;

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      cancelEvent(event);
      setOpened(!opened);
    }
  };

  const handleSelect = (value: string) => {
    onChange(value);
    setOpened(false);
  };

  // The `setTimeout()` is necessary to avoid what appears to be a React issue
  // that otherwise causes `handleSelect()` to be invoked immediately when the
  // dropdown is shown and any option other than the first is selected.
  // See: https://fiberplane.slack.com/archives/C01RFR1U3EC/p1677598061465209
  const onClick = () => setTimeout(() => setOpened(!opened), 10);

  return (
    <Root className={className}>
      <SelectButton
        {...attributes}
        buttonStyle="secondary"
        disabled={disabled}
        ref={containerRef}
        onClick={disabled ? noop : onClick}
        onKeyDown={handleKeyDown}
        role="combobox"
        tabIndex={disabled ? -1 : 0}
      >
        {LeadingIcon && <LeadingIcon />}

        <span>{selectedOption?.children ?? placeholder}</span>

        <AnimatedCaretIcon isActive={opened} />
        <input type="hidden" name={name} value={value} />

        {TrailingIcon && <TrailingIcon />}
      </SelectButton>

      <div role="listbox">
        <AnimatePresence>
          {opened && (
            <StyledAttachedPopup
              element={containerRef.current}
              placement={dropdownPlacement}
              offset={[0, 4]}
            >
              <DropdownMenu ref={modalRef}>
                {options.map((option) => (
                  <Option
                    key={option.value}
                    name={name}
                    onSelect={handleSelect}
                    selected={option === selectedOption}
                    {...option}
                  />
                ))}
              </DropdownMenu>
            </StyledAttachedPopup>
          )}
        </AnimatePresence>
      </div>
    </Root>
  );
}

const SelectButton = styled(Button)`
  padding: 8px;
  font: ${({ theme }) => theme.font.body.md.medium};
`;

export const StyledAttachedPopup = styled(AttachedPopup)`
  min-width: 100%;
  z-index: 1;
`;

export const DropdownMenu = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: 8px;
    overflow: hidden;
    justify-content: flex-start;
    align-items: flex-start;
    flex-grow: 1;
    background-color: ${theme.color.bg.elevated.default};
    border-radius: ${theme.radius.default};
    padding: 8px;
    box-shadow: ${theme.effect.shadow.m};
    width: 100%;
  `
);

export const Root = styled.div`
  height: min-content;
  position: relative;
`;
